/**
 * iOS Safari Polyfills and Compatibility Fixes
 * This file provides fallbacks for APIs not supported in iOS Safari
 */

// Polyfill for requestIdleCallback
if (!('requestIdleCallback' in window)) {
  (window as any).requestIdleCallback = (callback: () => void, options?: { timeout?: number }) => {
    const start = Date.now();
    return setTimeout(() => {
      callback();
    }, options?.timeout || 1);
  };
  
  (window as any).cancelIdleCallback = (id: number) => {
    clearTimeout(id);
  };
}

// Polyfill for fetchPriority (if not supported)
if (!('fetchPriority' in HTMLImageElement.prototype)) {
  Object.defineProperty(HTMLImageElement.prototype, 'fetchPriority', {
    get() {
      return this.getAttribute('fetchpriority') || 'auto';
    },
    set(value: 'high' | 'low' | 'auto') {
      this.setAttribute('fetchpriority', value);
    }
  });
}

// Polyfill for fetchPriority on link elements
if (!('fetchPriority' in HTMLLinkElement.prototype)) {
  Object.defineProperty(HTMLLinkElement.prototype, 'fetchPriority', {
    get() {
      return this.getAttribute('fetchpriority') || 'auto';
    },
    set(value: 'high' | 'low' | 'auto') {
      this.setAttribute('fetchpriority', value);
    }
  });
}

// iOS Safari specific fixes
export const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
export const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
export const isIOSSafari = isIOS && isSafari;

// Safe wrapper for APIs that might not be supported
export const safeRequestIdleCallback = (callback: () => void, options?: { timeout?: number }) => {
  if ('requestIdleCallback' in window) {
    try {
      return (window as any).requestIdleCallback(callback, options);
    } catch (error) {
      console.warn('requestIdleCallback failed, using setTimeout fallback:', error);
      return setTimeout(callback, options?.timeout || 100);
    }
  } else {
    return setTimeout(callback, options?.timeout || 100);
  }
};

// Safe wrapper for fetchPriority
export const safeSetFetchPriority = (element: HTMLElement, priority: 'high' | 'low' | 'auto') => {
  if ('fetchPriority' in element) {
    try {
      (element as any).fetchPriority = priority;
    } catch (error) {
      console.warn('fetchPriority failed, using attribute fallback:', error);
      element.setAttribute('fetchpriority', priority);
    }
  } else {
    element.setAttribute('fetchpriority', priority);
  }
};

// Console warning for iOS Safari users
if (isIOSSafari) {
  console.log('iOS Safari detected - using compatibility mode');
}
